import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';

import {
  IonButton,
  IonIcon
} from '@ionic/angular';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { addIcons } from 'ionicons';
import {
  addOutline,
  timeOutline,
  copyOutline,
  checkmarkOutline,
  chatbubblesOutline,
  closeOutline,
  expandOutline,
  contractOutline
} from 'ionicons/icons';
import { Router } from '@angular/router';
import { VoiceInputComponent } from '../voice-input/voice-input.component';
import { AuthService } from '../../services/auth.service';
import { AppSettingsService } from '../../services/app-settings.service';
import { ChatKitAgentService } from '../../services/chatkit-agent.service';
import { ChatService, ChatOverlayMode } from '../../services/chat.service';
import { NavigationHistoryService } from '../../services/navigation-history.service';
import { LoggerService } from '../../services/logger.service';
import {
  ChatKitOptions,
  OpenAIChatKit
} from '../../types/chatkit.types';
import { buildChatGreeting, CHAT_COMPOSER_PLACEHOLDER } from '../../utils/chat-surface-content';
import { extractChatPageContext } from '../../utils/chat-page-context';

interface PreviewThread {
  id: string;
  title: string;
  updated: string;
}

interface ChatKitThreadSummary {
  id: string;
  title: string | null;
  created_at: string;
}

interface ChatKitPage<T> {
  data: T[];
  has_more: boolean;
  after?: string | null;
}

interface ChatKitThreadsListRequest {
  type: 'threads.list';
  params: {
    limit: number;
    order: 'asc' | 'desc';
  };
  metadata?: Record<string, unknown>;
}

interface AiModelOption {
  aiModelId: string;
  modelCode: string;
  displayName: string;
  providerCode: string;
  providerDisplayName: string;
  speedTier?: string | null;
  qualityTier?: string | null;
  supportsToolCalling: boolean;
  supportsReasoning: boolean;
  supportsJsonMode: boolean;
  creditMultiplier: number;
  isBasicTier: boolean;
  isDefault: boolean;
}

interface AiModelsResponse {
  selectedModelCode?: string | null;
  selectedExecutionMode?: string | null;
  models: AiModelOption[];
}

/**
 * The single Solo chat surface. Hosted globally by the app shell (not routed)
 * so it is available on every authenticated page and its requests carry the
 * *underlying* page's context plus the recent navigation trail.
 *
 * Presentations:
 * - Desktop `panel` mode: right-side sidebar; threads open as an in-overlay drawer.
 * - Desktop `expanded` mode: large centered overlay with an inline threads column.
 * - Mobile (≤980px): fullscreen; threads open as a drawer. Mode is ignored.
 */
@Component({
    selector: 'app-chat-overlay',
    imports: [
    IonIcon,
    IonButton,
    VoiceInputComponent
],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './chat-overlay.component.html',
    styleUrls: ['./chat-overlay.component.scss']
})
export class ChatOverlayComponent implements OnInit, OnDestroy {
  @ViewChild('chatkitElement', { read: ElementRef })
  chatkitElement!: ElementRef<OpenAIChatKit>;

  // Execution mode UI has been removed; we always run in the advanced lane.
  private readonly taskMode = 'deep' as const;
  private readonly executionMode = 'advanced' as const;

  readonly starterPrompts = [
    { label: 'How is the business doing this week?', prompt: 'How is the business doing this week?', icon: 'chart' },
    { label: 'Find customers we have not reached in 3 weeks.', prompt: 'Find customers we have not reached in 3 weeks.', icon: 'search' },
    { label: 'Show overdue invoices and draft follow-ups.', prompt: 'Show overdue invoices and draft follow-ups.', icon: 'mail' },
    { label: 'Summarize today: cash, sales, and risk.', prompt: 'Summarize today: cash, sales, and risk.', icon: 'sparkle' }
  ];

  threads: PreviewThread[] = [];
  filteredThreads: PreviewThread[] = [];
  selectedThreadId: string | null = null;
  selectedModelCode: string | null = null;
  availableModels: AiModelOption[] = [];
  modelOptionsError: string | null = null;
  threadQuery = '';
  loadingThreads = false;
  loading = true;
  error: string | null = null;
  voiceInputEnabled = false;
  dictationEnabled = false;
  attachmentsEnabled = false;
  mode: ChatOverlayMode = 'panel';
  /** Panel/mobile only: whether the threads drawer is open (expanded shows it inline). */
  threadsOpen = false;
  threadIdCopied = false;

  private destroy$ = new Subject<void>();
  private pollTimer?: number;
  private threadChangeListener?: EventListener;
  private responseEndListener?: EventListener;
  private chatkitEffectListener?: EventListener;
  private chatkitErrorListener?: EventListener;
  private chatkitLogListener?: EventListener;

  constructor(
    private authService: AuthService,
    private appSettings: AppSettingsService,
    private chatkitService: ChatKitAgentService,
    private chatService: ChatService,
    private navHistory: NavigationHistoryService,
    private logger: LoggerService,
    private router: Router
  ) {
    addIcons({
      timeOutline,
      addOutline,
      copyOutline,
      checkmarkOutline,
      chatbubblesOutline,
      closeOutline,
      expandOutline,
      contractOutline
    });
  }

  ngOnInit(): void {
    this.dictationEnabled = this.appSettings.isFeatureEnabled('chatDictation');
    this.attachmentsEnabled = this.appSettings.isFeatureEnabled('chatAttachments');
    // The bolt-on mic is superseded by ChatKit's built-in composer dictation;
    // hide it whenever the built-in one is on so we never show two mics.
    this.voiceInputEnabled = this.appSettings.isFeatureEnabled('voiceInput') && !this.dictationEnabled;

    this.chatService.overlayMode
      .pipe(takeUntil(this.destroy$))
      .subscribe(mode => {
        this.mode = mode;
      });

    this.chatkitService.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => {
        if (error) {
          this.error = error;
          this.loading = false;
        }
      });

    this.chatService.pendingThreadId
      .pipe(takeUntil(this.destroy$))
      .subscribe(threadId => {
        if (threadId && !this.loading) {
          void this.applyPendingThread();
        }
      });

    this.initializeChat().catch(error => {
      this.logger.error('ChatOverlay', 'ngOnInit', 'Failed to initialize chat overlay', { error });
      this.error = 'Chat is unavailable right now. Please retry.';
      this.loading = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.pollTimer) {
      window.clearInterval(this.pollTimer);
    }

    const chatkit = this.chatkitElement?.nativeElement;
    if (chatkit && this.threadChangeListener) {
      chatkit.removeEventListener('chatkit.thread.change', this.threadChangeListener);
    }
    if (chatkit && this.responseEndListener) {
      chatkit.removeEventListener('chatkit.response.end', this.responseEndListener);
    }
    if (chatkit && this.chatkitEffectListener) {
      chatkit.removeEventListener('chatkit.effect', this.chatkitEffectListener);
    }
    if (chatkit && this.chatkitErrorListener) {
      chatkit.removeEventListener('chatkit.error', this.chatkitErrorListener);
    }
    if (chatkit && this.chatkitLogListener) {
      chatkit.removeEventListener('chatkit.log', this.chatkitLogListener);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.threadsOpen) {
      this.threadsOpen = false;
      return;
    }
    this.close();
  }

  close(): void {
    this.chatService.closeChat();
  }

  expandMode(): void {
    this.chatService.expandOverlay();
  }

  collapseMode(): void {
    this.chatService.collapseOverlay();
  }

  async onVoiceTranscript(text: string): Promise<void> {
    const chatkit = this.chatkitElement?.nativeElement;
    if (!chatkit) {
      this.logger.warn('ChatOverlay', 'onVoiceTranscript', 'ChatKit element not available');
      return;
    }

    try {
      await chatkit.setComposerValue({ text });
      await chatkit.focusComposer();
    } catch (error) {
      this.logger.error('ChatOverlay', 'onVoiceTranscript', 'Failed to set composer value', { error });
    }
  }

  toggleThreadsPanel(): void {
    this.threadsOpen = !this.threadsOpen;
  }

  closeThreadsPanel(): void {
    this.threadsOpen = false;
  }

  onThreadSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value || '';
    this.threadQuery = value;
    this.applyThreadFilter();
  }

  onModelChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedModelCode = value || null;
    void this.savePreferences();
  }

  /**
   * Returns a short badge label for a model's credit cost.
   * Basic-tier models show "Free"; premium models show their multiplier (e.g., "1×", "3×", "0.33×").
   */
  formatMultiplierBadge(model: AiModelOption): string {
    if (model.isBasicTier || model.creditMultiplier === 0) {
      return 'Free';
    }
    const m = Number(model.creditMultiplier);
    if (Number.isNaN(m)) return '1×';
    // Drop trailing zeros: 1.000 -> "1×", 0.330 -> "0.33×"
    const formatted = m.toFixed(2).replace(/\.?0+$/, '');
    return `${formatted}×`;
  }

  async copyThreadId(): Promise<void> {
    if (!this.selectedThreadId) return;
    try {
      await navigator.clipboard.writeText(this.selectedThreadId);
      this.threadIdCopied = true;
      setTimeout(() => { this.threadIdCopied = false; }, 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = this.selectedThreadId;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      this.threadIdCopied = true;
      setTimeout(() => { this.threadIdCopied = false; }, 2000);
    }
  }

  async selectThread(threadId: string): Promise<void> {
    this.chatkitService.setActiveThreadId(threadId);
    this.selectedThreadId = threadId;
    this.closeThreadsPanel();

    const chatkit = this.chatkitElement?.nativeElement;
    if (!chatkit) {
      return;
    }

    try {
      await chatkit.setThreadId(threadId);
    } catch (error) {
      this.logger.error('ChatOverlay', 'selectThread', 'Failed to select ChatKit thread', {
        error,
        threadId
      });
      this.error = 'This chat could not be displayed. Please retry.';
    }
  }

  async createThread(): Promise<void> {
    this.selectedThreadId = null;
    this.chatkitService.setActiveThreadId(null);
    this.closeThreadsPanel();

    const chatkit = this.chatkitElement?.nativeElement;
    if (!chatkit) {
      return;
    }

    try {
      await chatkit.setThreadId(null);
    } catch (error) {
      this.logger.error('ChatOverlay', 'createThread', 'Failed to open a new ChatKit thread', { error });
      this.error = 'A new chat could not be opened. Please retry.';
    }
  }

  async retry(): Promise<void> {
    this.error = null;
    this.loading = true;
    this.chatkitService.clearError();
    await this.initializeChat();
  }

  private applyThreadFilter(): void {
    const query = this.threadQuery.trim().toLowerCase();

    if (!query) {
      this.filteredThreads = [...this.threads];
      return;
    }

    this.filteredThreads = this.threads.filter(thread =>
      thread.title.toLowerCase().includes(query)
    );
  }

  private async initializeChat(): Promise<void> {
    await this.waitForChatKitScript();

    // Wait one turn so ViewChild is available after template render.
    await new Promise(resolve => setTimeout(resolve));

    const chatkit = this.chatkitElement?.nativeElement;
    if (!chatkit) {
      throw new Error('Chat component is not ready');
    }

    await this.loadModelOptionsAndPreferences();

    const session = await firstValueFrom(this.authService.authSession$);
    const firstName = session?.user?.name?.split(' ')[0] || '';
    const greeting = buildChatGreeting(firstName || null);

    const options: ChatKitOptions = {
      api: {
        url: this.appSettings.chatUrl,
        domainKey: this.appSettings.chatDomainKey,
        fetch: (url, init) => this.fetchWithToken(url, init),
        // two_phase: ChatKit calls attachments.create on our /chatkit endpoint,
        // then uploads the file bytes to the returned upload_descriptor URL.
        ...(this.attachmentsEnabled ? { uploadStrategy: { type: 'two_phase' as const } } : {})
      },
      theme: {
        colorScheme: this.getColorScheme(),
        color: {
          accent: {
            primary: this.getAccentColor(),
            level: 2
          }
        },
        radius: 'round'
      },
      header: {
        enabled: false
      },
      history: {
        enabled: false
      },
      startScreen: {
        greeting,
        prompts: this.starterPrompts
      },
      composer: {
        placeholder: CHAT_COMPOSER_PLACEHOLDER,
        attachments: this.attachmentsEnabled
          ? {
              enabled: true,
              maxCount: 5,
              maxSize: 20 * 1024 * 1024 // 20MB per file
            }
          : {
              enabled: false
            },
        dictation: {
          enabled: this.dictationEnabled
        }
      },
      threadItemActions: {
        feedback: false
      },
      onClientTool: (invocation) => this.chatkitService.onClientTool(invocation),
      widgets: {
        onAction: async (action, widgetItem) => {
          try {
            await this.chatkitService.handleWidgetAction(
              action,
              () => chatkit.sendCustomAction(action, widgetItem?.id)
            );
          } catch (error) {
            this.logger.error('ChatOverlay', 'widgets.onAction', 'Failed to forward widget action', {
              error,
              action,
              widgetItemId: widgetItem?.id
            });
          }
        }
      }
    };

    try {
      chatkit.setOptions(options);
    } catch {
      // ChatKit CDN rejected the options (commonly theme validation); retry without theme.
      const { theme: _theme, ...optionsWithoutTheme } = options;
      chatkit.setOptions(optionsWithoutTheme);
    }
    this.attachChatKitListeners(chatkit);
    this.chatkitService.setInitialized(true);
    await this.loadThreads();
    this.loading = false;
    this.error = null;

    const applied = await this.applyPendingThread();
    if (!applied) {
      // Resume the conversation the user was on last (survives close/reopen).
      const lastThreadId = this.chatkitService.getActiveThreadId();
      if (lastThreadId) {
        this.selectedThreadId = lastThreadId;
        try {
          await chatkit.setThreadId(lastThreadId);
        } catch (error) {
          this.logger.warn('ChatOverlay', 'initializeChat', 'Failed to resume last thread', {
            error,
            threadId: lastThreadId
          });
        }
      }
    }
  }

  /** Applies a thread requested via ChatService.openChatThread. Returns true when one was applied. */
  private async applyPendingThread(): Promise<boolean> {
    const threadId = this.chatService.consumePendingThreadId();
    if (!threadId) {
      return false;
    }

    await this.selectThread(threadId);
    return true;
  }

  private attachChatKitListeners(chatkit: OpenAIChatKit): void {
    this.threadChangeListener = (event: Event) => {
      const detail = (event as CustomEvent<{ threadId: string | null }>).detail;
      const nextThreadId = detail?.threadId ?? null;
      this.selectedThreadId = nextThreadId;
      this.chatkitService.setActiveThreadId(nextThreadId);
      void this.loadThreads();
    };

    this.responseEndListener = () => {
      void this.loadThreads();
    };

    this.chatkitEffectListener = (event: Event) => {
      const detail = (event as CustomEvent<{ name?: string; data?: unknown }>).detail;
      this.chatkitService.captureWorkflowMetadataEffect(detail?.name, detail?.data);
    };

    this.chatkitErrorListener = (event: Event) => {
      const detail = (event as CustomEvent<{ error: Error }>).detail;
      const chatkitError = detail?.error;
      this.logger.error('ChatOverlay', 'chatkit.error', 'ChatKit error event', {
        errorName: chatkitError?.name,
        errorMessage: chatkitError?.message
      });
    };

    this.chatkitLogListener = (event: Event) => {
      const detail = (event as CustomEvent<{ name: string; data?: Record<string, unknown> }>).detail;
      this.logger.info('ChatOverlay', 'chatkit.log', 'ChatKit diagnostic event', {
        name: detail?.name,
        data: detail?.data
      });
    };

    chatkit.addEventListener('chatkit.thread.change', this.threadChangeListener);
    chatkit.addEventListener('chatkit.response.end', this.responseEndListener);
    chatkit.addEventListener('chatkit.effect', this.chatkitEffectListener);
    chatkit.addEventListener('chatkit.error', this.chatkitErrorListener);
    chatkit.addEventListener('chatkit.log', this.chatkitLogListener);
  }

  private async loadThreads(): Promise<void> {
    this.loadingThreads = true;

    try {
      const payload: ChatKitThreadsListRequest = {
        type: 'threads.list',
        params: {
          limit: 50,
          order: 'desc'
        }
      };

      const page = await this.callChatKitApi<ChatKitPage<ChatKitThreadSummary>>(payload);
      this.threads = page.data.map(thread => this.mapThreadSummary(thread));
      this.applyThreadFilter();

      if (this.selectedThreadId && !this.threads.some(t => t.id === this.selectedThreadId)) {
        this.selectedThreadId = null;
      }
    } catch (error) {
      this.logger.error('ChatOverlay', 'loadThreads', 'Failed to load thread list', { error });
    } finally {
      this.loadingThreads = false;
    }
  }

  private async loadModelOptionsAndPreferences(): Promise<void> {
    const endpoint = this.buildApiUrl('/api/services/v1/ai/models');

    try {
      const response = await this.fetchWithToken(endpoint, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`Model list request failed: ${response.status}`);
      }

      const payload = await response.json() as AiModelsResponse;
      this.availableModels = Array.isArray(payload.models) ? payload.models : [];
      const selectedFromPayload = payload.selectedModelCode?.trim() || null;
      this.selectedModelCode = this.resolveSelectedModelCode(selectedFromPayload, this.availableModels);
      this.modelOptionsError = this.availableModels.length === 0
        ? 'No models are currently available for this workspace.'
        : null;
    } catch (error) {
      this.logger.warn(
        'ChatOverlay',
        'loadModelOptionsAndPreferences',
        'Failed to load model options. Using defaults.',
        { error }
      );
      this.availableModels = [];
      this.selectedModelCode = null;
      this.modelOptionsError = 'Unable to load model list right now.';
    }
  }

  private async savePreferences(): Promise<void> {
    const endpoint = this.buildApiUrl('/api/services/v1/ai/preferences');

    try {
      const response = await this.fetchWithToken(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          preferredModelCode: this.selectedModelCode,
          preferredExecutionMode: this.executionMode
        })
      });

      if (!response.ok) {
        throw new Error(`Preference update failed: ${response.status}`);
      }
    } catch (error) {
      this.logger.warn(
        'ChatOverlay',
        'savePreferences',
        'Failed to persist chat preferences.',
        { error }
      );
    }
  }

  private mapThreadSummary(thread: ChatKitThreadSummary): PreviewThread {
    return {
      id: thread.id,
      title: thread.title || 'Untitled chat',
      updated: this.toRelativeTime(thread.created_at)
    };
  }

  private toRelativeTime(isoDate: string): string {
    const date = new Date(isoDate);
    const diffMs = Date.now() - date.getTime();

    if (Number.isNaN(diffMs) || diffMs < 0) {
      return 'now';
    }

    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;

    const days = Math.floor(hours / 24);
    return `${days}d`;
  }

  private async callChatKitApi<T>(payload: object): Promise<T> {
    const response = await this.fetchWithToken(this.appSettings.chatUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Chat API failed: ${response.status}`);
    }

    return response.json() as Promise<T>;
  }

  private waitForChatKitScript(): Promise<void> {
    if (window.customElements?.get('openai-chatkit')) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const start = Date.now();
      this.pollTimer = window.setInterval(() => {
        if (window.customElements?.get('openai-chatkit')) {
          window.clearInterval(this.pollTimer);
          resolve();
          return;
        }

        if (Date.now() - start >= 10000) {
          window.clearInterval(this.pollTimer);
          reject(new Error('ChatKit script load timeout'));
        }
      }, 150);
    });
  }

  /**
   * ChatKit routes ALL its HTTP through this fetch — including two-phase
   * attachment uploads, which PUT file bytes directly to blob storage using a
   * SAS URL. Those requests must go out untouched: attaching our Bearer token
   * makes Azure reject them (400 InvalidAuthenticationInfo), and the Solo
   * header/body enrichment only applies to our own /chatkit API.
   */
  private isChatApiUrl(url: string): boolean {
    try {
      const target = new URL(url, window.location.origin);
      const chatApi = new URL(this.appSettings.chatUrl, window.location.origin);
      return target.origin === chatApi.origin;
    } catch {
      return false;
    }
  }

  private isTenantApiUrl(url: string): boolean {
    try {
      const target = new URL(url, window.location.origin);
      const tenantApi = new URL(this.appSettings.apiUrl, window.location.origin);
      return target.origin === tenantApi.origin;
    } catch {
      return false;
    }
  }

  private async fetchWithToken(url: string, init?: RequestInit): Promise<Response> {
    const isChatApiRequest = this.isChatApiUrl(url);
    if (!isChatApiRequest && !this.isTenantApiUrl(url)) {
      return fetch(url, init);
    }

    const session = await firstValueFrom(this.authService.authSession$);
    const token = session?.token;
    const headers = new Headers(init?.headers);
    const body = isChatApiRequest
      ? this.chatkitService.enrichRequestBody(init?.body, {
        solo: {
          task_mode: this.taskMode,
          execution_mode: this.executionMode,
          model_code: this.selectedModelCode,
          // The overlay is not routed, so router.url is the page underneath —
          // the context the user is actually looking at.
          page_context: extractChatPageContext(this.router.url),
          // Last 5 pages the user visited, oldest first — enough recency signal
          // for the assistant without bloating every request.
          nav_history: this.navHistory.getRecentTrail(5)
        }
      })
      : init?.body;

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    if (isChatApiRequest) {
      headers.set('X-Solo-Task-Mode', this.taskMode);
      headers.set('X-Solo-Execution-Mode', this.executionMode);
      if (this.selectedModelCode) {
        headers.set('X-Solo-Model', this.selectedModelCode);
      }
      headers.set('X-Solo-Client-Timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    }

    return fetch(url, {
      ...init,
      headers,
      body
    });
  }

  private resolveSelectedModelCode(
    selectedFromPayload: string | null,
    models: AiModelOption[]
  ): string | null {
    if (selectedFromPayload && models.some(m => m.modelCode === selectedFromPayload)) {
      return selectedFromPayload;
    }

    return models.find(model => model.isDefault)?.modelCode
      || models[0]?.modelCode
      || null;
  }

  private buildApiUrl(path: string): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const baseUrl = this.appSettings.apiUrl.replace(/\/+$/, '');

    if (normalizedPath.startsWith('/api/')) {
      if (baseUrl.endsWith('/api')) {
        return `${baseUrl}${normalizedPath.substring(4)}`;
      }

      return `${baseUrl}${normalizedPath}`;
    }

    return `${baseUrl}${normalizedPath}`;
  }

  private getColorScheme(): 'light' | 'dark' {
    if (typeof window === 'undefined') {
      return 'dark';
    }

    const textColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--ion-text-color-rgb')
      .trim();

    // Dark theme uses white text; anything else is a light theme. Matching
    // on white is stable across light-palette changes (the old check pinned
    // a specific light ink value and broke when the palette changed).
    return textColor === '255, 255, 255' ? 'dark' : 'light';
  }

  private getAccentColor(): string {
    if (typeof window === 'undefined') {
      return '#0E5B3A';
    }

    const accent = getComputedStyle(document.documentElement)
      .getPropertyValue('--ion-color-primary')
      .trim();

    return accent || '#0E5B3A';
  }
}
